import CT from '@marketplace-ai/tags';
import React from 'react';
import { ActorRefFrom } from 'xstate';
import { WizardStepsProps } from '~/components/Wizard';
import { JobListingDetail } from '~/types/hlx/jobListingDetail';
import { AdditionalTermsForm } from '../shared-steps/additional-terms/AdditionalTermsForm';
import { FacilityForm } from '../shared-steps/facilities/FacilityForm';
import { jobListingMachine } from './jobListing.machine';
import { BasicInfoForm } from './wizard-steps/basic-info/BasicInfoForm';
import { CompensationForm } from './wizard-steps/compensation/CompensationForm';
import { CostCalculator } from './wizard-steps/cost-calculator/CostCalculator';
import { LaborMetricsForm } from './wizard-steps/labor-metrics/LaborMetricsForm';
import { OnCallShiftDetailsForm } from './wizard-steps/on-call-shift-details/onCallShiftDetailsForm';
import { ReviewInfo } from './wizard-steps/review-info/ReviewInfo';
import { ShiftDetailsForm } from './wizard-steps/shift-details/ShiftDetailsForm';

const TAGS = CT.employer.jobListings.wizard;

export const createWizardSteps = (
  actor: ActorRefFrom<typeof jobListingMachine>,
  detail: JobListingDetail | undefined,
  loading: boolean
): WizardStepsProps[] => {
  const DEFAULT_PRIMARY_BTN = {
    label: 'Next',
    cyTag: TAGS.primaryBtn,
    isSubmit: true,
  };
  const DEFAULT_BACK_BTN = {
    label: 'Back',
    cyTag: TAGS.backBtn,
    onClick: () => {
      actor.send({ type: 'ON_PREV' });
    },
  };
  const SKIP_BTN = {
    label: 'Skip',
    cyTag: TAGS.secondaryBtn,
    onClick: () => {
      actor.send({ type: 'ON_SKIP' });
    },
  };
  return [
    {
      header: 'Step 1: Basic Listing Information',
      label: 'Basic Info',
      cyTag: TAGS.basicInfoStep,
      children: ({ footer }) => React.createElement(BasicInfoForm, { footer }),
      footer: {
        primaryButton: DEFAULT_PRIMARY_BTN,
        backButton: {
          label: 'Cancel',
          cyTag: TAGS.cancelBtn,
          onClick: () => {
            actor.send({ type: 'ON_PREV' });
          },
        },
      },
    },
    {
      header: 'Step 2: Facility Information',
      label: 'Facilities',
      cyTag: TAGS.facilitiesStep,
      children: ({ footer }) => React.createElement(FacilityForm, { footer, actor }),
      footer: {
        primaryButton: DEFAULT_PRIMARY_BTN,
        backButton: DEFAULT_BACK_BTN,
      },
    },
    {
      header: 'Step 3: Labor Metrics & Duration',
      label: 'Labor Metrics',
      cyTag: TAGS.laborMetricsStep,
      children: ({ footer }) => React.createElement(LaborMetricsForm, { footer }),
      footer: {
        primaryButton: DEFAULT_PRIMARY_BTN,
        backButton: DEFAULT_BACK_BTN,
      },
    },
    {
      header: 'Step 4: Base Compensation Structure',
      label: 'Compensation',
      cyTag: TAGS.compensationStep,
      children: ({ footer }) => React.createElement(CompensationForm, { footer }),
      footer: {
        primaryButton: DEFAULT_PRIMARY_BTN,
        backButton: DEFAULT_BACK_BTN,
      },
    },
    {
      header: 'Step 5: Shift Details (Optional)',
      label: 'Shift Details',
      cyTag: TAGS.shiftDetailsStep,
      children: ({ footer }) => React.createElement(ShiftDetailsForm, { footer }),
      footer: {
        primaryButton: DEFAULT_PRIMARY_BTN,
        backButton: DEFAULT_BACK_BTN,
        secondaryButton: SKIP_BTN,
      },
    },
    {
      header: 'Step 6: On-Call Shift Details (Optional)',
      label: 'On-Call Shift Details',
      cyTag: TAGS.onCallShiftDetailsStep,
      children: ({ footer }) => React.createElement(OnCallShiftDetailsForm, { footer }),
      footer: {
        primaryButton: DEFAULT_PRIMARY_BTN,
        backButton: DEFAULT_BACK_BTN,
        secondaryButton: SKIP_BTN,
      },
    },
    {
      header: 'Step 7: Cost Calculator',
      label: 'Cost Calculator',
      cyTag: TAGS.costCalculatorStep,
      children: ({ footer }) => React.createElement(CostCalculator, { footer, detail, loading }),
      footer: {
        primaryButton: {
          ...DEFAULT_PRIMARY_BTN,
          isSubmit: false,
          onClick: () => {
            actor.send({ type: 'ON_NEXT' });
          },
        },
        backButton: DEFAULT_BACK_BTN,
      },
    },
    {
      header: 'Step 8: Additional Terms (Optional)',
      label: 'Additional Terms',
      cyTag: TAGS.additionalInfoStep,
      children: ({ footer }) => React.createElement(AdditionalTermsForm, { footer, actor }),
      footer: {
        primaryButton: DEFAULT_PRIMARY_BTN,
        backButton: DEFAULT_BACK_BTN,
        secondaryButton: SKIP_BTN,
      },
    },
    {
      header: 'Step 9: Review & Submit',
      label: 'Review',
      cyTag: TAGS.reviewStep,
      children: ({ footer }) => React.createElement(ReviewInfo, { footer }),
      footer: {
        primaryButton: {
          label: 'Submit',
          cyTag: CT.employer.jobListings.wizard.primaryBtn,
          disabled: !detail?.id,
          onClick: () => {
            actor.send({ type: 'ON_SUBMIT_LISTING' });
          },
        },
        secondaryButton: {
          label: 'Save as Draft',
          cyTag: TAGS.secondaryBtn,
          onClick: () => {
            actor.send({ type: 'ON_SAVE_DRAFT' });
          },
        },
        backButton: {
          label: 'Back to Edit',
          cyTag: TAGS.backBtn,
          onClick: () => {
            actor.send({ type: 'ON_PREV' });
          },
        },
      },
    },
  ];
};
